import type { Team, TeamAlias } from '@/types';

const MIN_DATE = '0000-01-01';
const MAX_DATE = '9999-12-31';

const normalizeDateInput = (value?: string | null): string | null => {
  if (!value) return null;
  return value.length >= 10 ? value.slice(0, 10) : value;
};

const normalizeFromDate = (value?: string | null): string => {
  return normalizeDateInput(value) ?? MIN_DATE;
};

const normalizeToDate = (value?: string | null): string => {
  return normalizeDateInput(value) ?? MAX_DATE;
};

const compareDateStrings = (a: string, b: string): number => {
  if (a === b) return 0;
  return a > b ? 1 : -1;
};

const pickAliasForDate = (
  aliases: TeamAlias[] | undefined,
  targetDate: string | null
): TeamAlias | null => {
  if (!aliases?.length || !targetDate) return null;

  const sorted = sortTeamAliases(aliases);

  const effectiveAliases = sorted.filter(alias => {
    const from = normalizeFromDate(alias.validFrom ?? null);
    const to = normalizeToDate(alias.validTo ?? null);
    return targetDate >= from && targetDate <= to;
  });

  if (effectiveAliases.length > 0) {
    return effectiveAliases.reduce<TeamAlias | null>((best, alias) => {
      if (!best) return alias;

      const bestFrom = normalizeFromDate(best.validFrom ?? null);
      const aliasFrom = normalizeFromDate(alias.validFrom ?? null);
      const fromCompare = compareDateStrings(aliasFrom, bestFrom);

      if (fromCompare > 0) return alias;
      if (fromCompare === 0 && alias.isPrimary && !best.isPrimary) {
        return alias;
      }
      return best;
    }, null);
  }

  // 若没有覆盖指定日期的别名，回退到最近的历史别名
  const historicalAlias = sorted
    .filter(alias => {
      const from = normalizeFromDate(alias.validFrom ?? null);
      return from <= (targetDate ?? MIN_DATE);
    })
    .reduce<TeamAlias | null>((best, alias) => {
      if (!best) return alias;

      const bestFrom = normalizeFromDate(best.validFrom ?? null);
      const aliasFrom = normalizeFromDate(alias.validFrom ?? null);
      const fromCompare = compareDateStrings(aliasFrom, bestFrom);

      if (fromCompare > 0) return alias;
      if (fromCompare === 0 && alias.isPrimary && !best.isPrimary) {
        return alias;
      }
      return best;
    }, null);

  if (historicalAlias) {
    return historicalAlias;
  }

  // 如果日期早于第一条记录，返回首个或主别名
  return sorted.find(alias => alias.isPrimary) ?? sorted[0] ?? null;
};

const compareAliasByRange = (a: TeamAlias, b: TeamAlias): number => {
  const fromCompare = compareDateStrings(
    normalizeFromDate(a.validFrom ?? null),
    normalizeFromDate(b.validFrom ?? null)
  );
  if (fromCompare !== 0) return fromCompare;

  const toCompare = compareDateStrings(
    normalizeToDate(a.validTo ?? null),
    normalizeToDate(b.validTo ?? null)
  );
  if (toCompare !== 0) return toCompare;

  if (a.isPrimary === b.isPrimary) return 0;
  return a.isPrimary ? -1 : 1;
};

export const sortTeamAliases = (aliases: TeamAlias[]): TeamAlias[] => {
  if (!aliases.length) return aliases;
  return [...aliases].sort(compareAliasByRange);
};

/**
 * 获取战队显示名称
 * 优先使用中文名，否则使用英文名
 */
export const getTeamDisplayName = (team: Team): string => {
  return team.chineseName || team.englishName;
};

/**
 * 获取战队显示名称（可能为空的情况）
 */
export const getTeamDisplayNameSafe = (
  team: Team | null | undefined
): string => {
  return team ? getTeamDisplayName(team) : '无战队';
};

/**
 * 根据日期获取战队显示名称
 */
export const getTeamDisplayNameByDate = (
  team: Team | null | undefined,
  date?: string | null
): string => {
  if (!team) return '无战队';
  const normalizedTarget = normalizeDateInput(date ?? null);
  const alias = pickAliasForDate(team.aliases, normalizedTarget);

  if (alias) {
    return alias.alias;
  }

  return getTeamDisplayName(team);
};
